import { usePage, Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { useTheme, THEMES } from '../contexts/ThemeContext'

function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ''
}

function jobRunning(jobs) {
  return (jobs || []).some(j => j.state === 'running')
}

function DataUpdateMenu({ C, jobs, dataEmpty, onPosted }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const running = jobRunning(jobs)

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const trigger = async (path, confirmMsg, body) => {
    if (!confirm(confirmMsg)) return
    try {
      const res = await fetch(path, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : undefined,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok && json.error === 'already_running') {
        alert('既に実行中です')
      } else if (!res.ok) {
        alert(`実行に失敗しました${json.error ? `: ${json.error}` : ''}`)
      }
      onPosted?.()
    } catch (e) {
      alert(`通信エラー: ${e.message}`)
    }
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="データ更新メニュー"
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: running ? 'rgba(255,200,80,0.18)' : 'rgba(255,255,255,0.10)',
          border: 'none', color: C.navText, padding: '6px 12px', borderRadius: '4px',
          cursor: 'pointer', fontSize: '12px', fontWeight: 500,
        }}
      >
        <span style={{
          display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
          background: running ? '#f6c23e' : 'rgba(255,255,255,0.45)',
          boxShadow: running ? '0 0 0 3px rgba(246,194,62,0.25)' : 'none',
          animation: running ? 'pulse 1.4s ease-in-out infinite' : 'none',
        }} />
        データ更新
        <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: '320px',
          background: C.paper, color: C.ink, borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', border: `1px solid ${C.line}`,
          padding: '10px 0', zIndex: 100,
        }}>
          <div style={{ padding: '6px 14px 10px', borderBottom: `1px solid ${C.lineSoft}` }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.1em', color: C.inkMuted, fontWeight: 600 }}>
              ジョブ状態
            </div>
            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {(jobs || []).map(j => <JobRow key={j.key} job={j} C={C} />)}
            </div>
          </div>
          <button
            onClick={() => trigger('/data_updates/sync_bigquery', 'BigQuery から最新データを取得します。よろしいですか？')}
            style={menuItemStyle(C)}
          >
            <div style={{ fontWeight: 600, fontSize: '13px' }}>BigQuery から最新データを取得</div>
            <div style={{ fontSize: '11px', color: C.inkMuted, marginTop: '2px' }}>
              品目・商品・セット・レビューを一括同期
            </div>
          </button>
          <button
              onClick={() => trigger(
                '/data_updates/sync_bigquery',
                'BigQuery から全件データを取得します。よろしいですか？',
                { full: true },
              )}
              style={menuItemStyle(C)}
            >
              <div style={{ fontWeight: 600, fontSize: '13px' }}>BigQuery から全件取得</div>
              <div style={{ fontSize: '11px', color: C.inkMuted, marginTop: '2px' }}>
                set_plan_products / product_reviews も全件取得
              </div>
            </button>
          <button
            onClick={() => trigger('/data_updates/sync_master_xlsx', '旬カレンダーとレアリティスコアを Excel から投入します。よろしいですか？')}
            style={menuItemStyle(C)}
          >
            <div style={{ fontWeight: 600, fontSize: '13px' }}>基礎データ投入（旬・レアリティ）</div>
            <div style={{ fontSize: '11px', color: C.inkMuted, marginTop: '2px' }}>
              品目マスタ Excel から旬カレンダーとレアリティスコアを更新
            </div>
          </button>
          <button
            onClick={() => trigger('/data_updates/reevaluate_recent', '直近1週間のセットプランを再評価します。よろしいですか？', { range_days: 7 })}
            style={menuItemStyle(C)}
          >
            <div style={{ fontWeight: 600, fontSize: '13px' }}>直近1週間の評価値を再計算</div>
            <div style={{ fontSize: '11px', color: C.inkMuted, marginTop: '2px' }}>
              現在のしきい値・重みで再評価（軽量・最速）
            </div>
          </button>
          <button
            onClick={() => trigger('/data_updates/reevaluate_recent', '直近4週間のセットプランを再評価します。よろしいですか？', { range_days: 28 })}
            style={menuItemStyle(C)}
          >
            <div style={{ fontWeight: 600, fontSize: '13px' }}>直近4週間の評価値を再計算</div>
            <div style={{ fontSize: '11px', color: C.inkMuted, marginTop: '2px' }}>
              現在のしきい値・重みで再評価（軽量）
            </div>
          </button>
          <button
            onClick={() => trigger('/data_updates/reevaluate_recent', '直近1年のセットプランを再評価します。件数が多く時間がかかります。よろしいですか？', { range_days: 365 })}
            style={menuItemStyle(C)}
          >
            <div style={{ fontWeight: 600, fontSize: '13px' }}>直近1年の評価値を再計算</div>
            <div style={{ fontSize: '11px', color: C.inkMuted, marginTop: '2px' }}>
              現在のしきい値・重みで一括再評価（重い）
            </div>
          </button>
          <div style={{ borderTop: `1px solid ${C.lineSoft}`, marginTop: '4px', paddingTop: '4px' }}>
            <Link
              href="/job_runs"
              onClick={() => setOpen(false)}
              style={{ display: 'block', padding: '8px 14px', fontSize: '12px', color: C.inkMuted, textDecoration: 'none' }}
            >
              実行履歴を見る →
            </Link>
            <a
              href="/jobs_queue"
              onClick={() => setOpen(false)}
              style={{ display: 'block', padding: '8px 14px', fontSize: '12px', color: C.inkMuted, textDecoration: 'none' }}
            >
              ジョブキュー管理 →
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

function menuItemStyle(C) {
  return {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '10px 14px', border: 'none', background: 'transparent',
    color: C.ink, cursor: 'pointer', fontFamily: 'inherit',
  }
}

function JobRow({ job, C }) {
  const stateLabel = {
    idle:    { text: '待機', color: C.inkMuted },
    running: { text: '実行中', color: '#c89030' },
    done:    { text: '完了', color: C.greenDark },
    error:   { text: 'エラー', color: C.tomato || '#c0392b' },
  }[job.state] || { text: job.state, color: C.inkMuted }
  const pct = (job.total && job.total > 0 && job.done != null) ? Math.round((job.done / job.total) * 100) : null
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '2px 0' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 500 }}>{job.label}</div>
        {job.state === 'running' && job.total != null && (
          <div style={{ height: '4px', background: C.lineSoft, borderRadius: '2px', marginTop: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct || 0}%`, background: '#c89030', transition: 'width 0.3s' }} />
          </div>
        )}
        {job.state === 'running' && (
          <div style={{ fontSize: '10.5px', color: C.inkMuted, marginTop: '2px' }}>
            {job.done ?? 0}{job.total != null ? ` / ${job.total}` : ''}{job.errors ? ` (エラー ${job.errors})` : ''}
          </div>
        )}
        {job.state === 'done' && (
          <div style={{ fontSize: '10.5px', color: C.inkMuted, marginTop: '2px' }}>
            {job.done ?? 0}件処理{job.errors ? ` / エラー ${job.errors}` : ''}
          </div>
        )}
        {job.state === 'error' && (
          <div style={{ fontSize: '10.5px', color: C.tomato || '#c0392b', marginTop: '2px' }}>{job.error}</div>
        )}
      </div>
      <span style={{ fontSize: '10px', color: stateLabel.color, fontWeight: 600, letterSpacing: '0.08em' }}>
        {stateLabel.text}
      </span>
    </div>
  )
}

function UserMenu({ C, themeId, setThemeId, currentUser, onLogout }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: 'rgba(255,255,255,0.10)', border: 'none',
          color: C.navText, padding: '5px 10px', borderRadius: '4px',
          cursor: 'pointer', fontSize: '12px', fontWeight: 500,
        }}
      >
        {currentUser?.avatar_url
          ? <img src={currentUser.avatar_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />
          : <span style={{ display: 'inline-block', width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', lineHeight: '24px', textAlign: 'center', fontSize: '11px' }}>
              {currentUser?.name?.[0] || '?'}
            </span>
        }
        <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {currentUser?.name}
        </span>
        <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: '180px',
          background: C.paper, color: C.ink, borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', border: `1px solid ${C.line}`,
          padding: '6px 0', zIndex: 100,
        }}>
          <div style={{ padding: '6px 12px 8px', borderBottom: `1px solid ${C.lineSoft}` }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.1em', color: C.inkMuted, fontWeight: 600 }}>テーマ</div>
            <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {Object.values(THEMES).map(t => (
                <button
                  key={t.id}
                  onClick={() => { setThemeId(t.id); setOpen(false) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    textAlign: 'left', padding: '7px 8px', border: 'none', borderRadius: '5px',
                    background: themeId === t.id ? C.paper2 : 'transparent',
                    color: C.ink, cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px',
                    fontWeight: themeId === t.id ? 600 : 400,
                  }}
                >
                  <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: t.nav, boxShadow: '0 1px 3px rgba(0,0,0,0.3)', flexShrink: 0 }} />
                  {t.label}
                  {themeId === t.id && <span style={{ marginLeft: 'auto', fontSize: '12px', color: C.greenDark }}>✓</span>}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => { onLogout(); setOpen(false) }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '9px 14px', border: 'none', background: 'transparent',
              color: C.ink, cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px',
              marginTop: '2px',
            }}
          >
            ログアウト
          </button>
        </div>
      )}
    </div>
  )
}

export default function AppLayout({ children, title, bgColor, maxWidth, noPadding }) {
  const { current_user, data_update_jobs, data_empty } = usePage().props
  const { theme, themeId, setThemeId } = useTheme()
  const [jobs, setJobs] = useState(data_update_jobs || [])
  const [dataEmpty, setDataEmpty] = useState(Boolean(data_empty))

  useEffect(() => { setJobs(data_update_jobs || []) }, [data_update_jobs])
  useEffect(() => { setDataEmpty(Boolean(data_empty)) }, [data_empty])

  // 実行中のジョブがある間だけ 3 秒おきに状態を更新（どの画面でも自動更新）
  useEffect(() => {
    if (!jobRunning(jobs)) return
    let timer = null
    const tick = async () => {
      try {
        const res = await fetch('/data_updates/status', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        if (res.ok) {
          const json = await res.json()
          setJobs(json.jobs || [])
          if (typeof json.data_empty === 'boolean') setDataEmpty(json.data_empty)
        }
      } catch (_) { /* ignore */ }
      timer = setTimeout(tick, 3000)
    }
    timer = setTimeout(tick, 3000)
    return () => { if (timer) clearTimeout(timer) }
  }, [jobs])

  // 大画面では画面幅に合わせて拡大ズーム（設計幅 1100px 基準）
  useEffect(() => {
    const DESIGN = 1100
    const update = () => {
      const scale = window.innerWidth / DESIGN
      document.documentElement.style.zoom = scale > 1 ? String(scale.toFixed(4)) : ''
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const handleLogout = (e) => {
    e.preventDefault()
    router.post('/logout')
  }

  const refreshJobs = async () => {
    try {
      const res = await fetch('/data_updates/status', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      if (res.ok) {
        const json = await res.json()
        setJobs(json.jobs || [])
        if (typeof json.data_empty === 'boolean') setDataEmpty(json.data_empty)
      }
    } catch (_) { /* ignore */ }
  }

  const bg = bgColor || theme.bg

  const navLinkStyle = {
    color: theme.navText,
    opacity: 0.85,
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 600,
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg, fontFamily: "'Noto Sans JP', sans-serif" }}>
      <style>{`@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.5 } }`}</style>
      {/* Nav */}
      <nav style={{
        backgroundColor: theme.nav,
        color: theme.navText,
        padding: '0 24px',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
      }}>
        <Link href="/" style={{ color: theme.navText, textDecoration: 'none', fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/icon.png" alt="" style={{ width: 28, height: 28, borderRadius: '6px', flexShrink: 0 }} />
          野菜セット評価値シュミレータ
        </Link>
        <div style={{ flex: 1, display: 'flex', gap: '16px' }}>
          <Link href="/weekly_evaluations" style={navLinkStyle}>
            週次評価
          </Link>
          <Link href="/annual_retrospective" style={{ ...navLinkStyle, fontWeight: 400 }}>
            年間振り返り
          </Link>
          <Link href="/master/product_items" style={{ ...navLinkStyle, fontWeight: 400 }}>
            品目一覧
          </Link>
          <Link href="/item_analysis" style={{ ...navLinkStyle, fontWeight: 400 }}>
            品目分析
          </Link>
          <Link href="/evaluation_criteria" style={{ ...navLinkStyle, fontWeight: 400 }}>
            指標マスタ
          </Link>
        </div>

        <DataUpdateMenu C={theme} jobs={jobs} dataEmpty={dataEmpty} onPosted={refreshJobs} />

        {current_user && (
          <UserMenu
            C={theme}
            themeId={themeId}
            setThemeId={setThemeId}
            currentUser={current_user}
            onLogout={handleLogout}
          />
        )}
      </nav>

      {/* Content */}
      <main style={{ maxWidth: maxWidth || '1100px', margin: '0 auto', padding: noPadding ? '0' : '32px 24px' }}>
        {title && <h1 style={{ fontSize: '20px', fontWeight: 700, color: theme.ink, marginBottom: '24px' }}>{title}</h1>}
        {children}
      </main>
    </div>
  )
}
